import { FC, useMemo } from 'react';
import { Card, Col, Row, Typography } from 'antd';
import { TbArrowDownRight, TbArrowUpRight } from 'react-icons/tb';
import {
    Area,
    Bar,
    CartesianGrid,
    ComposedChart,
    Line,
    Tooltip as RechartsTooltip,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from 'recharts';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { FinancialValue, formatFinancialValue, useFinancialValueDisplay } from '@/shared/components/financial-values';
import { dayjs } from '@/utilities';

import { FinancialOverviewAreaChartProps, FinancialOverviewChartMetric } from '../../types';
import { FinancialOverviewChartEventMarker } from '../FinancialOverviewChartEventMarker';

interface FinancialOverviewTooltipPayloadItem {
    color?: string;
    dataKey?: unknown;
    payload?: {
        previousChange?: number;
    };
    name?: number | string;
    value?: unknown;
}

interface FinancialOverviewTooltipProps {
    active?: boolean;
    label?: string | number;
    payload?: readonly FinancialOverviewTooltipPayloadItem[];
}

const getTooltipScalarValue = (value: unknown): number | string | undefined => {
    if (typeof value === 'number' || typeof value === 'string') {
        return value;
    }

    if (Array.isArray(value)) {
        const firstValue = value[0];

        return typeof firstValue === 'number' || typeof firstValue === 'string' ? firstValue : undefined;
    }

    return undefined;
};

export const FinancialOverviewAreaChart: FC<FinancialOverviewAreaChartProps> = ({
    aspect = 2.1,
    comparisonSeries,
    data,
    chartRange,
    events,
    metric,
    showVolume,
    tickerSymbol,
}) => {
    const { token } = useAntDesignTheme();
    const { preference: financialValueDisplay } = useFinancialValueDisplay();
    const metricLabel = {
        price: 'Price',
        volume: 'Volume',
        changePercent: 'Change',
    }[metric];
    const comparisonColors = [token.purple5, token.cyan5, token.gold5, token.magenta5, token.geekblue5];
    const animationActive = data.length <= 400 && comparisonSeries.length === 0;
    const recessionEvents = useMemo(
        () => events.filter((event) => event.type === 'recessions' && event.endDate),
        [events],
    );
    const pointEventGroups = useMemo(() => {
        const groupedEvents = new Map<string, typeof events>();

        events
            .filter((event) => event.type !== 'recessions')
            .forEach((event) => {
                const chartDate = event.chartDate ?? event.date;

                groupedEvents.set(chartDate, [...(groupedEvents.get(chartDate) ?? []), event]);
            });

        return Array.from(groupedEvents.entries()).map(([date, grouped]) => ({ date, events: grouped }));
    }, [events]);
    const formatMetricValue = (value: unknown, selectedMetric: FinancialOverviewChartMetric = metric) => {
        const scalar = Number(getTooltipScalarValue(value));

        if (!Number.isFinite(scalar)) {
            return '-';
        }

        if (selectedMetric === 'volume') {
            return formatFinancialValue(scalar, financialValueDisplay, { kind: 'number' });
        }

        if (selectedMetric === 'changePercent') {
            return `${scalar.toLocaleString('en-US', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
            })}%`;
        }

        return formatFinancialValue(scalar, financialValueDisplay, { scalable: false });
    };

    const CustomTooltip = ({ active, payload, label }: FinancialOverviewTooltipProps) => {
        if (active && payload && payload.length) {
            const pricePayload = payload.find((item) => item.dataKey === 'price') ?? payload[0];
            const volumePayload = payload.find((item) => item.dataKey === 'volume');
            const comparisonPayload = payload.filter((item) => String(item.dataKey).startsWith('comparison:'));

            return (
                <Card bodyStyle={{ padding: '0.75rem 1rem', minWidth: 220 }}>
                    <Typography.Title
                        level={5}
                        style={{ marginBottom: 0, fontWeight: 'initial', color: token.colorTextTertiary }}
                    >
                        {chartRange !== 'realTime'
                            ? dayjs(label).format('MMM Do, YYYY')
                            : dayjs(label).format('h:mm A [ET]')}
                    </Typography.Title>
                    <div style={{ marginTop: '0.5rem' }}>
                        <Row align='middle' gutter={[8, 4]} wrap={false}>
                            <Col flex='auto'>
                                <span style={{ color: token.colorTextSecondary }}>{metricLabel}</span>
                            </Col>
                            <Col flex='none'>
                                <Typography.Text strong>
                                    {(pricePayload?.payload?.previousChange ?? 0) > 0 ? (
                                        <TbArrowUpRight
                                            size='1rem'
                                            style={{ verticalAlign: '-0.15rem', marginRight: '0.25rem' }}
                                            color={token.green4}
                                        />
                                    ) : (
                                        <TbArrowDownRight
                                            size='1rem'
                                            style={{ verticalAlign: '-0.15rem', marginRight: '0.25rem' }}
                                            color={token.red4}
                                        />
                                    )}
                                    {metric === 'price' &&
                                    Number.isFinite(Number(getTooltipScalarValue(pricePayload?.value))) ? (
                                        <FinancialValue
                                            scalable={false}
                                            value={Number(getTooltipScalarValue(pricePayload?.value))}
                                        />
                                    ) : (
                                        formatMetricValue(pricePayload?.value)
                                    )}
                                </Typography.Text>
                            </Col>
                        </Row>
                        {comparisonPayload.map((item) => {
                            const series = comparisonSeries.find((candidate) => candidate.dataKey === item.dataKey);

                            return (
                                <Row align='middle' gutter={[8, 4]} key={String(item.dataKey)} wrap={false}>
                                    <Col flex='auto'>
                                        <span style={{ color: token.colorTextSecondary }}>
                                            {series?.symbol ?? item.name}
                                        </span>
                                    </Col>
                                    <Col flex='none'>
                                        <Typography.Text>
                                            <FinancialValue
                                                scalable={false}
                                                value={Number(getTooltipScalarValue(item.value))}
                                            />
                                        </Typography.Text>
                                    </Col>
                                </Row>
                            );
                        })}
                        {showVolume && volumePayload?.value !== undefined && (
                            <Row align='middle' gutter={[8, 4]} wrap={false}>
                                <Col flex='auto'>
                                    <span style={{ color: token.colorTextSecondary }}>Volume</span>
                                </Col>
                                <Col flex='none'>
                                    <Typography.Text>
                                        <FinancialValue
                                            kind='number'
                                            value={Number(getTooltipScalarValue(volumePayload.value))}
                                        />
                                    </Typography.Text>
                                </Col>
                            </Row>
                        )}
                    </div>
                </Card>
            );
        }

        return null;
    };

    return (
        <div className='highlight-bar-charts' style={{ userSelect: 'none', width: '100%' }}>
            <ResponsiveContainer aspect={aspect} minHeight={260} width='100%'>
                <ComposedChart layout='horizontal' data={data} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                    <CartesianGrid
                        stroke={token.colorBorderSecondary}
                        strokeDasharray='5 5'
                        style={{ borderColor: token.colorBgBase }}
                    />
                    <defs>
                        <linearGradient id='financialOverviewBlueStroke' x1='0' y1='0' x2='0' y2='1'>
                            <stop offset='1%' stopColor={token.blue4} stopOpacity={0.25} />
                            <stop offset='99%' stopColor={token.blue4} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <XAxis
                        axisLine={{ stroke: token.colorTextQuaternary }}
                        tick={{ fill: token.colorTextTertiary }}
                        includeHidden
                        allowDataOverflow
                        minTickGap={16}
                        tickFormatter={(value: string) =>
                            chartRange !== 'realTime' ? dayjs(value).format('MMM D, YYYY') : dayjs(value).format('LT')
                        }
                        interval='preserveStart'
                        dataKey='name'
                        height={32}
                        tickMargin={8}
                    />
                    <YAxis
                        yAxisId='price'
                        axisLine={{ stroke: token.colorTextQuaternary }}
                        tick={{ fill: token.colorTextTertiary }}
                        includeHidden
                        allowDataOverflow
                        interval='preserveStartEnd'
                        tickFormatter={(value: string) => formatMetricValue(value)}
                        orientation='right'
                        type='number'
                        domain={[(dataMin: number) => dataMin * 0.875, (dataMax: number) => dataMax * 1.125]}
                        padding={{ top: 0, bottom: 0 }}
                        tickMargin={0}
                        width={96}
                        tickLine={false}
                        style={{ color: token.colorTextSecondary }}
                    />
                    {showVolume && (
                        <YAxis
                            yAxisId='volume'
                            hide
                            type='number'
                            domain={[0, (dataMax: number) => dataMax * 4]}
                            padding={{ top: 0, bottom: 0 }}
                        />
                    )}
                    <RechartsTooltip
                        content={({ active, label, payload }) => (
                            <CustomTooltip
                                active={active}
                                label={typeof label === 'string' || typeof label === 'number' ? label : undefined}
                                payload={payload}
                            />
                        )}
                    />
                    {recessionEvents.map((event) => (
                        <ReferenceArea
                            fill={token.colorFillSecondary}
                            fillOpacity={0.5}
                            ifOverflow='extendDomain'
                            key={`${event.type}:${event.date}`}
                            label={<FinancialOverviewChartEventMarker events={[event]} tickerSymbol={tickerSymbol} />}
                            strokeOpacity={0}
                            x1={event.chartDate ?? event.date}
                            x2={event.chartEndDate ?? event.endDate}
                            yAxisId='price'
                        />
                    ))}
                    {pointEventGroups.map((eventGroup) => {
                        const groupTypes = new Set(eventGroup.events.map((event) => event.type));
                        const primaryType = eventGroup.events[0]?.type;

                        return (
                            <ReferenceLine
                                ifOverflow='extendDomain'
                                key={eventGroup.date}
                                label={
                                    <FinancialOverviewChartEventMarker
                                        events={eventGroup.events}
                                        tickerSymbol={tickerSymbol}
                                    />
                                }
                                stroke={
                                    groupTypes.size > 1
                                        ? token.colorPrimary
                                        : primaryType === 'news'
                                          ? token.gold6
                                          : token.green6
                                }
                                strokeDasharray='3 3'
                                strokeOpacity={0.45}
                                x={eventGroup.date}
                                yAxisId='price'
                            />
                        );
                    })}
                    {showVolume && (
                        <Bar
                            animationDuration={300}
                            dataKey='volume'
                            fill={token.colorTextTertiary}
                            isAnimationActive={animationActive}
                            opacity={0.18}
                            yAxisId='volume'
                        />
                    )}
                    <Area
                        animationDuration={300}
                        type='monotone'
                        dataKey='price'
                        stroke={token.blue4}
                        fillOpacity={1}
                        fill='url(#financialOverviewBlueStroke)'
                        isAnimationActive={animationActive}
                        yAxisId='price'
                    />
                    {comparisonSeries.map((series, index) => (
                        <Line
                            animationDuration={300}
                            connectNulls
                            dataKey={series.dataKey}
                            dot={false}
                            isAnimationActive={animationActive}
                            key={series.dataKey}
                            name={series.label}
                            stroke={comparisonColors[index % comparisonColors.length]}
                            strokeWidth={1.5}
                            type='monotone'
                            yAxisId='price'
                        />
                    ))}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};
